import api from './api';

/**
 * Dispute Service
 * Handles all API calls related to dispute management
 */

// ────────────────────────────────────────────────────────────────────────────
// User Dispute Operations
// ────────────────────────────────────────────────────────────────────────────

/**
 * File a new dispute
 * @param {FormData} formData - Must include: title, reason, disputeType, serviceType,
 *                               description. Optional: respondentId, targetId,
 *                               attachments[], disputedAmount, currency
 * @returns {Promise<Object>} Created dispute DTO
 */
export const fileDispute = async (formData) => {
  const response = await api.post('/v1/api/disputes', formData, {
    headers: {
      'Content-Type': 'multipart/form-data'
    }
  });
  return response.data;
};

/**
 * Get dispute by ID
 * @param {string} disputeId - UUID of the dispute
 * @returns {Promise<Object>} Dispute DTO
 */
export const getDisputeById = async (disputeId) => {
  const response = await api.get(`/v1/api/disputes/${disputeId}`);
  return response.data;
};

/**
 * Get dispute by dispute number
 * @param {string} disputeNumber - Unique dispute number (e.g., "DSP-2024-001234")
 * @returns {Promise<Object>} Dispute DTO
 */
export const getDisputeByNumber = async (disputeNumber) => {
  const response = await api.get(`/v1/api/disputes/number/${disputeNumber}`);
  return response.data;
};

/**
 * Get disputes filed by current user
 * @param {number} page - Page number (0-indexed)
 * @param {number} size - Page size
 * @returns {Promise<Object>} Paginated dispute list
 */
export const getMyFiledDisputes = async (page = 0, size = 10) => {
  const response = await api.get('/v1/api/disputes/my/filed', {
    params: { page, size }
  });
  return response.data;
};

/**
 * Get disputes filed against current user
 * @param {number} page - Page number (0-indexed)
 * @param {number} size - Page size
 * @returns {Promise<Object>} Paginated dispute list
 */
export const getDisputesAgainstMe = async (page = 0, size = 10) => {
  const response = await api.get('/v1/api/disputes/my/against', {
    params: { page, size }
  });
  return response.data;
};

// ────────────────────────────────────────────────────────────────────────────
// Admin Dispute Operations
// ────────────────────────────────────────────────────────────────────────────

/**
 * Get all disputes (Admin only)
 * @param {number} page - Page number (0-indexed)
 * @param {number} size - Page size
 * @returns {Promise<Object>} Paginated dispute list
 */
export const getAllDisputes = async (page = 0, size = 10) => {
  const response = await api.get('/v1/api/disputes', {
    params: { page, size }
  });
  return response.data;
};

/**
 * Get disputes by status (Admin only)
 * @param {string} status - Dispute status (OPEN, UNDER_REVIEW, ESCALATED, etc.)
 * @param {number} page - Page number (0-indexed)
 * @param {number} size - Page size
 * @returns {Promise<Object>} Paginated dispute list
 */
export const getDisputesByStatus = async (status, page = 0, size = 10) => {
  const response = await api.get(`/v1/api/disputes/status/${status}`, {
    params: { page, size }
  });
  return response.data;
};

/**
 * Get disputes by group (Admin only)
 * @param {string} groupUuid - UUID of the group
 * @param {number} page - Page number (0-indexed)
 * @param {number} size - Page size
 * @returns {Promise<Object>} Paginated dispute list
 */
export const getDisputesByGroup = async (groupUuid, page = 0, size = 10) => {
  const response = await api.get(`/v1/api/disputes/group/${groupUuid}`, {
    params: { page, size }
  });
  return response.data;
};

/**
 * Get disputes by campaign (Admin only)
 * @param {string} campaignUuid - UUID of the campaign
 * @param {number} page - Page number (0-indexed)
 * @param {number} size - Page size
 * @returns {Promise<Object>} Paginated dispute list
 */
export const getDisputesByCampaign = async (campaignUuid, page = 0, size = 10) => {
  const response = await api.get(`/v1/api/disputes/campaign/${campaignUuid}`, {
    params: { page, size }
  });
  return response.data;
};

/**
 * Get disputes by event (Admin only)
 * @param {string} eventUuid - UUID of the event
 * @param {number} page - Page number (0-indexed)
 * @param {number} size - Page size
 * @returns {Promise<Object>} Paginated dispute list
 */
export const getDisputesByEvent = async (eventUuid, page = 0, size = 10) => {
  const response = await api.get(`/v1/api/disputes/event/${eventUuid}`, {
    params: { page, size }
  });
  return response.data;
};

/**
 * Update dispute status (Admin only)
 * @param {string} disputeId - UUID of the dispute
 * @param {Object} updateDTO - { status: string, resolutionNote?: string }
 * @returns {Promise<Object>} Updated dispute DTO
 */
export const updateDisputeStatus = async (disputeId, updateDTO) => {
  const response = await api.patch(`/v1/api/disputes/${disputeId}/status`, updateDTO);
  return response.data;
};

export default {
  fileDispute,
  getDisputeById,
  getDisputeByNumber,
  getMyFiledDisputes,
  getDisputesAgainstMe,
  getAllDisputes,
  getDisputesByStatus,
  getDisputesByGroup,
  getDisputesByCampaign,
  getDisputesByEvent,
  updateDisputeStatus
};
